import { DiaryCompletenessCard } from "./DiaryCompletenessCard";
import { ProvisionalTargetNote } from "./ProvisionalTargetNote";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { Flame, Target, Utensils, Info, ArrowRight } from "lucide-react";
import { Card, SectionLabel } from "@/components/ui/Card";
import { getCalorieBalance } from "@/api/nutrition";
import { cn } from "@/lib/utils";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { LoadError } from "@/components/ui/LoadError";

function n(v: number | null): string {
  return v == null ? "—" : v.toLocaleString("ru-RU", { maximumFractionDigits: 0 });
}

interface Props {
  /** "full" (default) on /nutrition — big numbers + macros + methodology.
   *  "compact" on Dashboard — smaller card, no macros, "Details →" link. */
  variant?: "full" | "compact";
}

export function CalorieBalanceCard({ variant = "full" }: Props = {}) {
  const { t } = useTranslation();
  const q = useQuery({
    queryKey: ["nutrition-balance"],
    queryFn: getCalorieBalance,
    staleTime: 20_000,
  });
  const [showWhy, setShowWhy] = useState(false);

  if (q.isLoading) {
    return (
      <Card>
        <SectionLabel>{t("nutrition.balance_today")}</SectionLabel>
        <p className="mt-3 text-[13px] text-taupe">{t("nutrition.loading")}</p>
      </Card>
    );
  }
  if (q.isError || !q.data) return <LoadError onRetry={() => void q.refetch()} retrying={q.isFetching} />;

  const d = q.data;
  const intake = d.intake.calories;
  const target = d.target.calories;
  const burn = d.expenditure.training_burn;
  const remaining = d.target.remaining;
  const isCompact = variant === "compact";
  if (d.meta.source_unavailable || target == null || burn == null || remaining == null || d.expenditure.total == null) {
    return <Card>
      <div className="flex items-center justify-between">
        <SectionLabel>{t("nutrition.balance_today")}</SectionLabel>
        {isCompact && <Link to="/nutrition" className="text-[11px] text-amber-dark">{t("nutrition.details")}</Link>}
      </div>
      <p className="mt-3 text-sm text-taupe">{t("workout_detail.metrics_unavailable_note")}</p>
      <div className="mt-3 grid grid-cols-3 gap-2">
        <CompactBox label={t("nutrition.eaten")} value={n(intake)} tone="navy" />
        <CompactBox label={t("nutrition.workout_burn")} value="—" tone="navy" />
        <CompactBox label={t("nutrition_target_basis.completed_label")} value="—" tone="navy" />
      </div>
      <p className="mt-2 text-[12px] text-taupe">{t("calorie_balance.base_plus_workout", { base: n(d.expenditure.baseline_tdee), burn: "—" })}</p>
      <ProvisionalTargetNote quality={d.meta} />
      <DiaryCompletenessCard date={d.date} completeness={d.completeness} compact={isCompact} />
      {!isCompact && <div className="mt-3 grid grid-cols-3 gap-3 text-[12px]">
        <MacroRow label={t("calorie_balance.macro_protein")} cur={d.intake.protein_g} target={null} />
        <MacroRow label={t("calorie_balance.macro_carbs")} cur={d.intake.carbs_g} target={null} />
        <MacroRow label={t("calorie_balance.macro_fat")} cur={d.intake.fat_g} target={null} />
      </div>}
    </Card>;
  }
  const pct = target > 0 ? Math.min(120, (intake / target) * 100) : 0;
  const over = pct > 100;

  const remainingLabel =
    remaining > 0
      ? t("nutrition.remaining_kcal", { n: n(remaining) })
      : t("nutrition.over_kcal", { n: n(Math.abs(remaining)) });

  const evaluated = d.completeness?.status === "complete" && d.balance.net_kcal != null;
  const goalStatus = evaluated ? (d.balance.goal_status ?? "unknown") : "unknown";
  const statusLabel = t(`diary_quality.${goalStatus}`);
  const statusCls = "text-taupe";

  if (isCompact) {
    return (
      <Card>
        <div className="flex items-center justify-between">
          <SectionLabel>{t("nutrition.balance_today")}</SectionLabel>
          <Link
            to="/nutrition"
            className="inline-flex items-center gap-1 text-[11px] font-medium text-amber-dark hover:underline"
          >
            {t("nutrition.details")}
            <ArrowRight className="h-3 w-3" strokeWidth={2} />
          </Link>
        </div>

        <div className="mt-3 grid grid-cols-3 gap-2">
          <CompactBox label={t("nutrition.eaten")} value={n(intake)} tone="navy" />
          <CompactBox
            label={t("nutrition.workout_burn")}
            value={burn > 0 ? `+${n(burn)}` : "0"}
            tone="amber"
          />
          <CompactBox label={t("nutrition_target_basis.completed_label")} value={n(target)} tone="navy" />
        </div>

        <p className="mt-2 text-[12px] text-taupe">{t("nutrition_target_basis.completed_note")}</p>
        <ProvisionalTargetNote quality={d.meta} />
        <DiaryCompletenessCard date={d.date} completeness={d.completeness} compact={isCompact} />
        <div className="mt-3">
          <div className="mb-1 flex justify-between text-[10px] text-taupe">
            <span>{t("nutrition.to_goal")}</span>
            <span>{remainingLabel}</span>
          </div>
          <div className="h-1.5 overflow-hidden rounded-full bg-cream-border">
            <div
              className={cn(
                "h-full rounded-full transition-all",
                over ? "bg-coral" : "bg-amber",
              )}
              style={{ width: `${Math.min(100, pct)}%` }}
            />
          </div>
        </div>

        <div className="mt-3 flex items-center justify-between border-t border-cream-border pt-2 text-[11px]">
          <span className="text-taupe">
            {t("nutrition.burn")} <span className="metric-number text-navy-deep">{n(d.expenditure.total)}</span>
          </span>
          <span className={cn("font-medium", statusCls)}>
            {statusLabel}
          </span>
        </div>
      </Card>
    );
  }

  return (
    <Card>
      <SectionLabel>{t("calorie_balance.today_balance")}</SectionLabel>

      <div className="mt-4 grid grid-cols-3 gap-3">
        <BigBox
          icon={<Utensils className="h-3.5 w-3.5" strokeWidth={1.8} />}
          label={t("calorie_balance.eaten")}
          value={n(intake)}
          suffix={t("calorie_balance.kcal")}
          tone="navy"
        />
        <BigBox
          icon={<Flame className="h-3.5 w-3.5" strokeWidth={1.8} />}
          label={t("calorie_balance.training")}
          value={burn > 0 ? `+${n(burn)}` : "0"}
          suffix={t("calorie_balance.kcal")}
          tone="amber"
        />
        <BigBox
          icon={<Target className="h-3.5 w-3.5" strokeWidth={1.8} />}
          label={t("nutrition_target_basis.completed_label")}
          value={n(target)}
          suffix={t("calorie_balance.kcal")}
          tone="navy"
        />
      </div>

      <p className="mt-2 text-[12px] text-taupe">{t("nutrition_target_basis.completed_note")}</p>
      <ProvisionalTargetNote quality={d.meta} />
        <DiaryCompletenessCard date={d.date} completeness={d.completeness} compact={isCompact} />
      <div className="mt-4">
        <div className="mb-1 flex justify-between text-[11px] text-taupe">
          <span>{t("calorie_balance.to_goal")}</span>
          <span>{remainingLabel}</span>
        </div>
        <div className="h-2 overflow-hidden rounded-full bg-cream-border">
          <div
            className={cn(
              "h-full rounded-full transition-all",
              over ? "bg-coral" : "bg-amber",
            )}
            style={{ width: `${Math.min(100, pct)}%` }}
          />
        </div>
      </div>

      <div className="mt-4 flex items-center justify-between border-t border-cream-border pt-3 text-[12px]">
        <span className="text-taupe">
          {t("calorie_balance.total_burn")} <span className="metric-number text-navy-deep">{n(d.expenditure.total)}</span>{" "}
          {t("calorie_balance.kcal")}
          <span className="ml-1 text-[11px] text-taupe/70">
            {t("calorie_balance.base_plus_workout", { base: n(d.expenditure.baseline_tdee), burn: n(burn) })}
          </span>
        </span>
        <span className={cn("font-medium", statusCls)}>
          {statusLabel}
        </span>
      </div>

      <div className="mt-3 grid grid-cols-3 gap-3 text-[12px]">
        <MacroRow
          label={t("calorie_balance.macro_protein")}
          cur={d.intake.protein_g}
          target={d.target.protein_g}
        />
        <MacroRow
          label={t("calorie_balance.macro_carbs")}
          cur={d.intake.carbs_g}
          target={d.target.carbs_g}
        />
        <MacroRow label={t("calorie_balance.macro_fat")} cur={d.intake.fat_g} target={d.target.fat_g} />
      </div>

      <button
        type="button"
        onClick={() => setShowWhy((v) => !v)}
        className="mt-3 inline-flex items-center gap-1 text-[11px] text-taupe hover:text-navy-deep"
      >
        <Info className="h-3 w-3" strokeWidth={1.8} />
        {showWhy ? t("calorie_balance.hide_methodology") : t("calorie_balance.show_methodology")}
      </button>
      {showWhy ? (
        <p className="mt-2 rounded-md border border-cream-border bg-cream-light p-3 text-[11px] leading-relaxed text-taupe">
          {d.meta.notes}
          {" · "}
          {t("calorie_balance.methodology_text", { bmr: n(d.expenditure.bmr), weight: d.meta.weight_kg_used, target: d.meta.target_weight_kg ? t("calorie_balance.target_weight_inline", { n: d.meta.target_weight_kg }) : "", deficit: n(d.target.deficit_applied) })}
        </p>
      ) : null}
    </Card>
  );
}

function CompactBox({
  label,
  value,
  tone,
}: {
  label: string;
  value: string;
  tone: "navy" | "amber";
}) {
  const { t } = useTranslation();
  return (
    <div className="rounded-md bg-cream px-2 py-2 text-center">
      <div className="text-[9px] uppercase tracking-wider text-taupe">
        {label}
      </div>
      <div
        className={cn(
          "metric-number mt-0.5 text-[18px] font-medium",
          tone === "amber" ? "text-amber-dark" : "text-navy-deep",
        )}
      >
        {value}
      </div>
    </div>
  );
}


function BigBox({
  icon,
  label,
  value,
  suffix,
  tone,
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
  suffix: string;
  tone: "navy" | "amber";
}) {
  const { t } = useTranslation();
  return (
    <div className="rounded-md bg-cream px-3 py-3 text-center">
      <div className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider text-taupe">
        {icon}
        {label}
      </div>
      <div
        className={cn(
          "metric-number mt-1 text-[24px] font-medium md:text-[26px]",
          tone === "amber" ? "text-amber-dark" : "text-navy-deep",
        )}
      >
        {value}
      </div>
      <div className="text-[10px] text-taupe">{suffix}</div>
    </div>
  );
}

function MacroRow({
  label,
  cur,
  target,
}: {
  label: string;
  cur: number;
  target: number | null;
}) {
  const { t } = useTranslation();
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider text-taupe">
        {label}
      </div>
      <div className="mt-0.5 text-[12px]">
        <span className="metric-number text-navy-deep">{n(cur)}</span>
        <span className="text-taupe"> / {n(target)}{t("nutrition_page.g")}</span>
      </div>
    </div>
  );
}
